import React from 'react';
import {
  CheckCircle2,
  Hash,
  Calendar,
  ToggleLeft,
  Mail,
  Link,
  Paperclip,
  FileCode,
  Type,
  CornerDownLeft,
} from 'lucide-react';
import { DataTableColumn } from '../../../types/grid';
import { getColumnName } from './types';
import { Button } from '../../ui/Button';

interface FilterSearchColumnMenuProps<T extends Record<string, any>> {
  matchingColumns: DataTableColumn<T>[];
  inputValue: string;
  highlightedIndex: number;
  onSelectColumn: (col: DataTableColumn<T>) => void;
  onCommitSearchText: (text: string) => void;
  onHoverIndex: (idx: number) => void;
}

export function FilterSearchColumnMenu<T extends Record<string, any>>({
  matchingColumns,
  inputValue,
  highlightedIndex,
  onSelectColumn,
  onCommitSearchText,
  onHoverIndex,
}: FilterSearchColumnMenuProps<T>) {
  const getColumnIcon = (col: DataTableColumn<T>) => {
    if (col.id === 'status' || col.dataType === 'status') {
      return <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />;
    }
    if (col.dataType === 'number') return <Hash size={13} className="text-amber-500 shrink-0" />;
    if (col.dataType === 'date') return <Calendar size={13} className="text-sky-500 shrink-0" />;
    if (col.dataType === 'boolean') return <ToggleLeft size={13} className="text-purple-500 shrink-0" />;
    if (col.dataType === 'email') return <Mail size={13} className="text-blue-500 shrink-0" />;
    if (col.dataType === 'url') return <Link size={13} className="text-indigo-500 shrink-0" />;
    if (col.dataType === 'file') return <Paperclip size={13} className="text-orange-500 shrink-0" />;
    if (col.dataType === 'json') return <FileCode size={13} className="text-teal-500 shrink-0" />;
    return <Type size={13} className="text-zinc-400 shrink-0" />;
  };

  return (
    <div>
      <div className="px-2.5 py-1 text-xs font-semibold text-cf-textMuted uppercase tracking-wider">
        Filter by column
      </div>
      <div className="max-h-56 overflow-y-auto space-y-0.5 py-0.5">
        {matchingColumns.length === 0 && !inputValue.trim() && (
          <div className="px-3 py-2 text-xs text-cf-textMuted italic">
            No matching columns found
          </div>
        )}
        {matchingColumns.map((col, idx) => {
          const isSelected = highlightedIndex === idx;
          return (
            <Button
              key={col.id}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSelectColumn(col)}
              onMouseEnter={() => onHoverIndex(idx)}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded text-left text-xs transition-colors ${
                isSelected
                  ? 'bg-cf-surfaceHover text-cf-textPrimary font-medium'
                  : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {getColumnIcon(col)}
                <span className="truncate">{getColumnName(col)}</span>
              </div>
              <span className="text-xs text-cf-textMuted uppercase shrink-0 font-sans">
                {col.dataType || 'text'}
              </span>
            </Button>
          );
        })}
      </div>

      {/* Option to search as freeform text */}
      {inputValue.trim() && (
        <div className={matchingColumns.length > 0 ? 'border-t border-cf-border mt-1 pt-1' : ''}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCommitSearchText(inputValue)}
            onMouseEnter={() => onHoverIndex(matchingColumns.length)}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-xs transition-colors ${
              highlightedIndex === matchingColumns.length
                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-1 ring-blue-500/30 font-medium'
                : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
            }`}
          >
            <span className="font-mono text-xs font-semibold text-cf-textMuted flex items-center shrink-0">
              T=
            </span>
            <span className="truncate">
              Search for this text
            </span>
            <CornerDownLeft size={11} className="ml-auto text-cf-textMuted shrink-0" />
          </Button>
        </div>
      )}
    </div>
  );
}
